'use client';

import Button from '@/components/ui/Button';

import { serializeJsonLd } from '@/lib/json-ld';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { usePostHog } from 'posthog-js/react';
import { capturePosthogEvent } from '@/lib/posthog';

// The marketing-page NavCube is a Three.js component; load it client-only
// so the simple page's first paint isn't gated on the WebGL bundle, and so
// it doesn't run during SSR.
const NavCube = dynamic(() => import('../(landing)/components/NavCube'), {
  ssr: false,
  loading: () => <div className="w-full h-full" />,
});

interface Star {
  top: number;
  left: number;
  size: string;
  color: string;
  rotate: string;
}

// Random, non-overlapping star pattern with a mix of large and small stars
const NUM_STARS = 110;
const MIN_DIST = 7; // percent, minimum distance between stars
const SIZES = [
  'w-8 h-8', 'w-10 h-10', 'w-12 h-12', 'w-14 h-14', 'w-16 h-16', 'w-20 h-20',
  'w-24 h-24', 'w-28 h-28', 'w-32 h-32', 'w-40 h-40', // allow some large
];
const COLORS = [
  'text-primary','text-secondary'
];
const ROTATIONS = ['rotate-0', 'rotate-3', 'rotate-6', 'rotate-12', '-rotate-3', '-rotate-6', '-rotate-12', 'rotate-45', '-rotate-45'];

function randomStar(existing: Star[]): Star {
  let tries = 0;
  while (tries < 100) {
    const top = Math.random() * 95 + 2; // 2% to 97%
    const left = Math.random() * 95 + 2;
    // Prevent overlap: check distance to all existing
    if (existing.every((s: Star) => {
      const dx = s.left - left;
      const dy = s.top - top;
      return Math.sqrt(dx * dx + dy * dy) > MIN_DIST;
    })) {
      return {
        top,
        left,
        size: SIZES[Math.floor(Math.random() * SIZES.length)] + ' md:' + SIZES[Math.floor(Math.random() * SIZES.length)],
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
      };
    }
    tries++;
  }
  // fallback: just place it
  return {
    top: Math.random() * 95 + 2,
    left: Math.random() * 95 + 2,
    size: SIZES[Math.floor(Math.random() * SIZES.length)] + ' md:' + SIZES[Math.floor(Math.random() * SIZES.length)],
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    rotate: ROTATIONS[Math.floor(Math.random() * ROTATIONS.length)],
  };
}

// Built on the client only - uses Math.random(), so running it during SSR
// would produce a different arrangement than the client and trigger a
// hydration mismatch on every star <svg>.
function buildStarPattern(): Star[] {
  const pattern: Star[] = [];
  for (let i = 0; i < NUM_STARS; i++) {
    const isTop = i < Math.floor(NUM_STARS * 2 / 3);
    let star: Star;
    let tries = 0;
    do {
      star = randomStar(pattern);
      if (isTop && star.top > 50) star.top = Math.random() * 45 + 2;
      tries++;
    } while (pattern.some(s => Math.sqrt((s.left - star.left) ** 2 + (s.top - star.top) ** 2) < MIN_DIST) && tries < 10);
    pattern.push(star);
  }
  return pattern;
}

const faqItems = [
  {
    question: "What makes Votely better than the Political Compass test?",
    answer: "Votely adds a third axis, for social views, to the usual economic and governance pair. Instead of dropping you into one of four quadrants, it places you in 1 of 81 named ideologies."
  },
  {
    question: "Why is a 3-axis political quiz more accurate?",
    answer: "A 2D compass leaves out the social dimension. By adding a Progressive-to-Conservative axis to the economic and governance ones, Votely tells a culturally progressive libertarian apart from a socially conservative socialist."
  },
  {
    question: "How does the scoring system work?",
    answer: "You answer on three core axes, and a second phase adds six sub-axes tuned to your region. Together they measure 57 dimensions and place you among 81 ideologies. That is more than 8values, 9axes, or 12axes measure."
  },
  {
    question: "What is the 3D cube visualization?",
    answer: "Instead of a flat chart, Votely shows your result in an interactive 3D cube. You can rotate it to see your exact position and how you relate to all 81 ideologies."
  },
  {
    question: "Why use percentage sliders instead of yes/no answers?",
    answer: "Political views are not binary. A slider from 0 to 100% records how strongly you hold each view, so agreeing 30% with one statement and 90% with another come out differently."
  },
  {
    question: "How long does the quiz take?",
    answer: "The short version is 12 questions and takes about two minutes. The long version is 60 questions, takes about ten to twelve minutes, and unlocks the report."
  },
  {
    question: "Should I take the 12 or 60 question version?",
    answer: "The 12-question version gives you a quick read that is easy to share. The 60-question version is more precise, and it is the one the full report is built from."
  },
  {
    question: "Who created Votely and why?",
    answer: "Jules Lemée built Votely because a four-box compass felt too crude for how people think about politics. You can reach him through the feedback form on your results page."
  }
];

export default function HomeClient() {
  const posthog = usePostHog();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [starPattern, setStarPattern] = useState<Star[]>([]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot client-only generation, mirrors StarBackground pattern
    setStarPattern(buildStarPattern());
  }, []);

  const handleFaqToggle = (index: number) => {
    const isCurrentlyOpen = openFaqIndex === index;
    const nextIndex = isCurrentlyOpen ? null : index;
    setOpenFaqIndex(nextIndex);
    capturePosthogEvent(posthog, 'faq_toggled', {
      question: faqItems[index].question,
      is_open: !isCurrentlyOpen
    });
  };

  const handleLogoClick = () => {
    capturePosthogEvent(posthog, 'external_link_clicked', {
      destination: 'https://getvotely.com',
      location: 'hero_logo'
    }, { sendToServer: true });
  };

  const handleQuizStart = (quizType: 'short' | 'long') => {
    capturePosthogEvent(posthog, 'quiz_started', {
      quiz_type: quizType
    }, { sendToServer: true });
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Random, Non-overlapping Star Pattern - Fixed background */}
      <div className="fixed inset-0 pointer-events-none">
        {starPattern.map((star, i) => (
          <svg
            key={i}
            className={`absolute opacity-80 ${star.size} ${star.color} ${star.rotate}`}
            style={{
              top: `${star.top}%`,
              left: `${star.left}%`,
            }}
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <polygon points="12,2 15,10 23,10 17,15 19,23 12,18 5,23 7,15 1,10 9,10" />
          </svg>
        ))}
      </div>

      {/* Content wrapper */}
      <div className="relative flex flex-col items-center py-16">
        {/* Main Quiz Card */}
        <div className="w-full max-w-4xl mx-auto mb-8 px-4">
          <div className="relative rounded-3xl bg-white/80 shadow-xl backdrop-blur-sm p-8 flex flex-col items-center">
            {/* Logo: full 3D NavCube - the marketing-page brand mark. Three.js
                renders the same cube as the marketing navbar but at large
                size, so the user can grab and rotate it. The NavCube root
                hard-sizes itself from the size prop (192px).

                The wrapper MUST reserve that exact 192x192 box (w-48 h-48).
                NavCube is loaded `ssr: false` with a `w-full h-full`
                placeholder; with no height on the parent that placeholder
                collapsed to 0px, then jumped to 192px when the WebGL chunk
                mounted: a 192px layout shift that pushed the entire page
                (title, quiz buttons, FAQ) down and drove field CLS to Poor.
                Reserving the box keeps the placeholder and the cube the same
                size, so CLS stays 0. */}
            <div className="mb-6 w-48 h-48" onClick={handleLogoClick}>
              <NavCube size={192} cameraZoom={1.5} />
            </div>
            {/* Title */}
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 text-center">
              Take the Votely quiz
            </h1>
            {/* Both quiz options visible upfront - no intermediate Start
                Quiz button. The old gated flow added a click for no benefit. */}
            <p className="text-center text-foreground/70 mb-4">
              Pick a length. If you start short, most of your answers carry over to the long quiz on this device.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md md:max-w-lg">
              <Button
                href="/quiz?type=short"
                onClick={() => handleQuizStart('short')}
                className="flex-1 flex-col gap-0 py-5 text-lg md:text-xl"
              >
                Short quiz
                <span className="text-sm font-semibold opacity-90">(12 questions)</span>
              </Button>
              <Button
                href="/quiz?type=long"
                onClick={() => handleQuizStart('long')}
                className="flex-1 flex-col gap-0 py-5 text-lg md:text-xl"
              >
                Long quiz
                <span className="text-sm font-semibold opacity-90">(60 questions)</span>
              </Button>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="w-full max-w-4xl mx-auto px-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-8">
          <h2 className="text-3xl font-bold text-center mb-8 text-foreground">Before you start</h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                <button
                  onClick={() => handleFaqToggle(index)}
                  className="w-full text-left flex justify-between items-center py-2 hover:text-secondary transition-colors"
                >
                  <h3 className="text-lg font-semibold pr-4">{item.question}</h3>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openFaqIndex === index && (
                  <p className="mt-3 text-slate-600 leading-relaxed">{item.answer}</p>
                )}
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* FAQ Schema Markup */}
      <Script
        id="faq-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })
        }}
      />
    </div>
  );
}
